import React, { useState, useEffect, useContext, useCallback } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';
import { GroupMoneyTransactions, TrackedManagers } from './GroupMoney';

const GROUPS = ['Elite', 'Master - 1', 'Master - 2', 'Master - 3', 'Master - 4', 'Master - 5'];

const fmtCash = (v) => v == null ? '—' : v.toLocaleString('en-US');

// periodStart/End and lastRaceEarningsAt are UTC "YYYY-MM-DDTHH:mm:ss" —
// just reformat the string, no Date()/timezone conversion (backend is
// always UTC, converting via local Date would silently shift it).
const fmtDateTime = (iso) => iso ? iso.replace('T', ' ').slice(0, 16) : '—';

const CONFIDENCE_STYLE = {
  high: { color: '#16a34a', label: 'high confidence — matched a single catalog item' },
  medium: { color: '#d97706', label: 'medium confidence — matched a combination of 2 items' },
  low: { color: '#d97706', label: 'low confidence — matched a combination of 3 items' },
  speculative: { color: 'var(--c-text-light)', label: 'speculative — matched a combination of 4+ items, many degrees of freedom', faint: true },
  none: { color: 'var(--c-text-light)', label: 'no spend' },
  unresolved: { color: 'var(--c-error)', label: 'unresolved' },
};

const FOBASE_DRIVER_FIELDS = [
  { key: 'overall', label: 'Overall' }, { key: 'concentration', label: 'Concentration' },
  { key: 'talent', label: 'Talent' }, { key: 'aggressiveness', label: 'Aggressiveness' },
  { key: 'experience', label: 'Experience' }, { key: 'technicalInsight', label: 'Tech Insight' },
  { key: 'stamina', label: 'Stamina' }, { key: 'charisma', label: 'Charisma' },
  { key: 'motivation', label: 'Motivation' }, { key: 'reputation', label: 'Reputation' },
  { key: 'weight', label: 'Weight' },
];

const MARKET_DRIVER_FIELDS = [
  { key: 'overall', label: 'Overall' }, { key: 'concentration', label: 'Concentration' },
  { key: 'talent', label: 'Talent' }, { key: 'experience', label: 'Experience' },
  { key: 'aggressiveness', label: 'Aggressiveness' }, { key: 'ti', label: 'Tech Insight' },
  { key: 'stamina', label: 'Stamina' }, { key: 'charisma', label: 'Charisma' },
  { key: 'motivation', label: 'Motivation' }, { key: 'reputation', label: 'Reputation' },
  { key: 'age', label: 'Age' }, { key: 'weight', label: 'Weight' },
  { key: 'salary', label: 'Salary' }, { key: 'fee', label: 'Fee' }, { key: 'offers', label: 'Offers' },
];

const StatGrid = ({ fields, values }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '4px 16px', fontSize: 12 }}>
    {fields.map(f => (
      <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ color: 'var(--c-text-muted)' }}>{f.label}</span>
        <span style={{ fontWeight: 600 }}>{values[f.key] ?? '—'}</span>
      </div>
    ))}
  </div>
);

const PeriodRow = ({ p }) => {
  const conf = CONFIDENCE_STYLE[p.confidence] || {};
  return (
    <tr>
      <td style={{ fontWeight: 600 }}>R{p.raceNumber}</td>
      <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDateTime(p.periodStart)} → {fmtDateTime(p.periodEnd)}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {fmtCash(p.cashStart)} → {fmtCash(p.cashEnd)}
        <span style={{ marginLeft: 6, fontSize: 11, color: p.cashDelta < 0 ? 'var(--c-error)' : '#16a34a' }}>
          ({p.cashDelta > 0 ? '+' : ''}{fmtCash(p.cashDelta)})
        </span>
      </td>
      <td title="Rough flat estimate for staff/facilities upkeep only (~950,000) — driver/TD salary and tyre costs are billed separately on race day, not between races, so they are no longer part of this figure.">{fmtCash(p.recurringCostEstimate)}</td>
      <td>{fmtCash(p.discretionarySpend)}</td>
      <td>
        {p.confidence === 'unresolved' ? (
          <span style={{ color: conf.color, fontStyle: 'italic' }} title="Couldn't match this amount to any known price or price pair — likely several purchases at once, and/or facility upgrades (no price table available for those).">
            not determined
          </span>
        ) : (p.guessedPurchases || []).length > 0 ? (
          <span style={{ fontStyle: 'italic', fontSize: conf.faint ? 11 : undefined, opacity: conf.faint ? 0.7 : 1 }}
            title={`Guess only, not a confirmed fact — ${conf.label}`}>
            {p.guessedPurchases.join(', ')} <span style={{ color: conf.color }}>?</span>
          </span>
        ) : (
          <span style={{ color: 'var(--c-text-light)' }}>—</span>
        )}
      </td>
      <td>{p.carLevelDelta ? (p.carLevelDelta > 0 ? `+${p.carLevelDelta}` : p.carLevelDelta) : '—'}</td>
    </tr>
  );
};

// ── Expanded manager detail (spending history) ─────────────────────────

const ManagerDetail = ({ manager }) => {
  const periods = manager.racePeriods || [];
  const current = periods.find(p => p.current);
  const closed = periods.filter(p => !p.current);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {manager.lastRaceEarnings != null && (
        <div>
          <div style={{
            display: 'inline-block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.5px', color: '#fff', background: '#16a34a',
            padding: '3px 8px', borderRadius: 'var(--radius-sm)', marginBottom: 8,
          }}>
            Earned in last race (R{manager.lastRaceEarningsRaceNumber})
          </div>
          <div style={{ fontSize: 13 }}>
            <strong style={{ color: '#16a34a' }}>+{fmtCash(manager.lastRaceEarnings)}</strong>
            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--c-text-muted)' }}>
              at {fmtDateTime(manager.lastRaceEarningsAt)}
            </span>
          </div>
        </div>
      )}
      {periods.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
          No race periods yet — data accumulates day by day between snapshots.
        </div>
      ) : (
        <>
          {current && (
            <div>
              <div style={{
                display: 'inline-block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.5px', color: '#fff', background: 'var(--c-accent)',
                padding: '3px 8px', borderRadius: 'var(--radius-sm)', marginBottom: 8,
              }}>
                Current race (R{current.raceNumber}) — spent so far
              </div>
              <table className="fb-table fb-table-sm">
                <thead>
                  <tr>
                    <th>Race</th><th>Period</th><th>Cash (start → end)</th><th>Staff/Facilities (est.)</th>
                    <th>Discretionary</th><th>Guessed purchases</th><th>Car level Δ</th>
                  </tr>
                </thead>
                <tbody><PeriodRow p={current} /></tbody>
              </table>
            </div>
          )}

          {closed.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--c-text-muted)', marginBottom: 6 }}>
                Closed race periods
              </div>
              <table className="fb-table fb-table-sm">
                <thead>
                  <tr>
                    <th>Race</th><th>Period</th><th>Cash (start → end)</th><th>Staff/Facilities (est.)</th>
                    <th>Discretionary</th><th>Guessed purchases</th><th>Car level Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {closed.map(p => <PeriodRow key={p.raceNumber} p={p} />)}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {manager.matchedFobaseDriver && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--c-text-muted)', marginBottom: 6 }}>
            Fobase driver profile — as of S{manager.matchedFobaseDriver.asOfSeason}R{manager.matchedFobaseDriver.asOfRaceNumber}
          </div>
          <StatGrid fields={FOBASE_DRIVER_FIELDS} values={manager.matchedFobaseDriver} />
        </div>
      )}
    </div>
  );
};

// ── Driver market record (click to reveal, per manager) ────────────────

const DriverMarketPanel = ({ record }) => (
  <div style={{ background: '#fafafa', padding: 14, borderTop: '1px solid var(--c-border-light)' }}>
    {!record ? (
      <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>No market data for this driver.</div>
    ) : (
      <>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--c-text-muted)', marginBottom: 6 }}>
          Driver market record — {record.name} ({record.nat}) — noted {record.noted}
        </div>
        <StatGrid fields={MARKET_DRIVER_FIELDS} values={record} />
      </>
    )}
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────

const GroupSpending = () => {
  const { userRole } = useContext(AuthContext);
  const canView = ['ADMIN', 'FORCE_ONE'].includes(userRole);
  const canCollect = userRole === 'ADMIN';

  const [group, setGroup] = useState(GROUPS[0]);
  const [view, setView] = useState('spending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [expandedDriverMarket, setExpandedDriverMarket] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [collectMsg, setCollectMsg] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    setExpanded(null);
    setExpandedDriverMarket(null);
    try {
      const res = await api.get(`/group-spending/${encodeURIComponent(group)}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load group spending.');
    } finally {
      setLoading(false);
    }
  }, [group, canView]);

  useEffect(() => { load(); }, [load]);

  const handleCollectNow = async () => {
    setCollecting(true);
    setCollectMsg(null);
    try {
      await api.post('/group-spending/collect-now');
      await load();
      setCollectMsg('Collected.');
    } catch (err) {
      setCollectMsg(err.response?.status === 403 ? 'Access denied.' : 'Collection failed.');
    } finally {
      setCollecting(false);
    }
  };

  if (!canView) return null;

  const tabStyle = (active) => ({
    padding: '8px 16px', cursor: 'pointer', border: 'none', background: 'none',
    fontWeight: active ? 700 : 400, fontSize: 13,
    color: active ? 'var(--c-accent)' : 'var(--c-text-muted)',
    borderBottom: `2px solid ${active ? 'var(--c-accent)' : 'transparent'}`,
    marginBottom: -2, transition: 'color 0.15s', whiteSpace: 'nowrap',
  });

  const getCurrentPeriod = (m) => (m.racePeriods || []).find(p => p.current);

  const SORT_GETTERS = {
    cash: (m) => m.currentCash,
    earned: (m) => m.lastRaceEarnings,
    spentThisRace: (m) => { const c = getCurrentPeriod(m); return c ? Math.abs(c.cashDelta) : null; },
    carLevelRank: (m) => m.carLevelRank,
    driverOa: (m) => m.driverOa,
    tdOa: (m) => m.tdOa,
    staffOa: (m) => m.staffOa,
  };

  const handleSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortableTh = ({ label, k }) => (
    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(k)}>
      {label}{sortKey === k ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
    </th>
  );

  const sortedManagers = (() => {
    const managers = data?.managers || [];
    if (!sortKey) return managers;
    const getter = SORT_GETTERS[sortKey];
    return [...managers].sort((a, b) => {
      const av = getter(a), bv = getter(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  })();

  return (
    <>
      <div className="fb-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="fb-page-title">Group Spending</div>
          <div className="fb-page-sub">
            Daily cash snapshots, spend broken down against known GPRO prices — a heuristic, not a fact.
          </div>
        </div>
        {canCollect && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {collectMsg && <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{collectMsg}</span>}
            <button type="button" className="fb-btn fb-btn-secondary fb-btn-sm" disabled={collecting} onClick={handleCollectNow}>
              {collecting ? 'Collecting…' : 'Collect Now'}
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {[['spending', 'Spending'], ['transactions', 'Transactions'], ['tracked', 'Tracked managers']].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setView(k)}
                  className={`fb-btn fb-btn-sm ${view === k ? 'fb-btn-primary' : 'fb-btn-secondary'}`}>{label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', borderBottom: '2px solid var(--c-border)', marginBottom: 16, overflowX: 'auto' }}>
        {GROUPS.map(g => (
          <button key={g} style={tabStyle(group === g)} onClick={() => setGroup(g)}>{g}</button>
        ))}
      </div>

      {view === 'transactions' && <GroupMoneyTransactions group={group} canCollect={canCollect} />}
      {view === 'tracked' && <TrackedManagers group={group} canEdit={canCollect} />}

      {view === 'spending' && loading && <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
      {view === 'spending' && !loading && error && <div style={{ color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}

      {view === 'spending' && !loading && !error && data && (
        <div className="fb-card">
          <div className="fb-card-header">
            <span className="fb-card-title">
              {data.group} · S{data.season} · last snapshot {data.lastSnapshotDate}
            </span>
          </div>
          <div className="fb-table-wrap ra-table-wrap">
            <table className="fb-table fb-table-sm ra-table" style={{ whiteSpace: 'nowrap' }}>
              <thead>
                <tr>
                  <th>Manager</th>
                  <SortableTh label="Cash" k="cash" />
                  <SortableTh label="Earned" k="earned" />
                  <SortableTh label="Spent (this race)" k="spentThisRace" />
                  <SortableTh label="Car Level" k="carLevelRank" />
                  <th>Car Level Δ</th>
                  <SortableTh label="Driver" k="driverOa" />
                  <SortableTh label="TD" k="tdOa" />
                  <SortableTh label="Staff OA" k="staffOa" />
                </tr>
              </thead>
              <tbody>
                {sortedManagers.map((m) => {
                  const isExpanded = expanded === m.idm;
                  const isDriverExpanded = expandedDriverMarket === m.idm;
                  const current = getCurrentPeriod(m);
                  return (
                    <React.Fragment key={m.idm}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(isExpanded ? null : m.idm)}>
                        <td style={{ fontWeight: 600 }}>
                          {m.managerName}
                          {m.matchedFobaseDriver && (
                            <span title="This manager is also a Fobase user" style={{ marginLeft: 6, fontSize: 10, color: 'var(--c-accent)' }}>★</span>
                          )}
                        </td>
                        <td>{fmtCash(m.currentCash)}</td>
                        <td title={m.lastRaceEarnings != null ? `Earned in R${m.lastRaceEarningsRaceNumber} at ${fmtDateTime(m.lastRaceEarningsAt)}` : undefined}>
                          {m.lastRaceEarnings != null ? (
                            <span style={{ color: '#16a34a' }}>+{fmtCash(m.lastRaceEarnings)}</span>
                          ) : '—'}
                        </td>
                        <td title="Cash delta over the current, still-ongoing race period">
                          {current ? fmtCash(Math.abs(current.cashDelta)) : '—'}
                        </td>
                        <td>
                          {m.carLevel ?? '—'}
                          {m.carLevelRank != null && <span style={{ color: 'var(--c-text-muted)', fontSize: 11 }}> (#{m.carLevelRank})</span>}
                          {m.carType && (
                            <span style={{
                              marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 5px',
                              borderRadius: 4, background: '#f3f4f6', color: 'var(--c-text-muted)',
                            }}>{m.carType}</span>
                          )}
                        </td>
                        <td title="Car level at the start vs end of the current race period">
                          {current ? `${current.carLevelStart} → ${current.carLevelEnd}` : '—'}
                        </td>
                        <td>
                          {m.driverName ?? '—'}
                          {m.driverOa != null && <span style={{ color: 'var(--c-text-muted)', fontSize: 11 }}> ({m.driverOa} OA)</span>}
                          {m.driverMarketRecord && (
                            <button type="button" title="Driver characteristics from the drivers market table"
                              onClick={(e) => { e.stopPropagation(); setExpandedDriverMarket(isDriverExpanded ? null : m.idm); }}
                              style={{
                                marginLeft: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                                border: '1px solid var(--c-border)', borderRadius: 4, padding: '1px 5px',
                                background: isDriverExpanded ? 'var(--c-accent)' : '#fff',
                                color: isDriverExpanded ? '#fff' : 'var(--c-text-muted)',
                              }}>
                              Driver
                            </button>
                          )}
                          <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{fmtCash(m.driverSalary)}/race</div>
                        </td>
                        <td>
                          {m.tdName ?? '—'}
                          {m.tdOa != null && <span style={{ color: 'var(--c-text-muted)', fontSize: 11 }}> ({m.tdOa} OA)</span>}
                          <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{fmtCash(m.tdSalary)}/race</div>
                        </td>
                        <td>{m.staffOa ?? '—'}</td>
                      </tr>
                      {isDriverExpanded && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0 }}>
                            <DriverMarketPanel record={m.driverMarketRecord} />
                          </td>
                        </tr>
                      )}
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} style={{ background: '#fafafa', padding: 14 }}>
                            <ManagerDetail manager={m} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {sortedManagers.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: 20 }}>No managers.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default GroupSpending;
